<template>
  <div class="cv-root">

    <!-- ══ Header ═══════════════════════════════════════════ -->
    <header class="cv-header">
          <button class="back-btn" @click="$router.push('/chat/search_chat')">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <div class="cv-header__user">
        <div class="cv-avatar-wrap">
          <img
            src="@/assets/images/avatars/avatar.png"
            alt=""
            class="cv-avatar"
            aria-hidden="true"
          />
          <span class="cv-status-dot" aria-label="Online"></span>
        </div>
        <div class="cv-header__meta">
          <span class="cv-header__name">{{ clientName }}</span>
          <span class="cv-header__role">
            {{ userType === 'topup' ? 'Top-up account' : 'Client' }}
          </span>
        </div>
      </div>

      <div class="cv-manager-chip" aria-label="You are the manager">
        <i class="fas fa-crown" aria-hidden="true"></i>
        <span>Manager</span>
      </div>
    </header>

    <!-- ══ Messages ═════════════════════════════════════════ -->
    <main class="cv-messages" ref="chatMessages" aria-label="Conversation">
      <div class="cv-messages__list">

        <div
          v-for="(msg, index) in messages"
          :key="`${msg.id || msg.timestamp}-${index}`"
          class="cv-row"
          :class="msg.sender === managerUsername ? 'cv-row--sent' : 'cv-row--received'"
        >
          <div
            class="cv-bubble"
            :class="msg.sender === managerUsername ? 'cv-bubble--sent' : 'cv-bubble--received'"
          >
            <!-- ── Text ──────────────────────────────────── -->
            <p
              v-if="msg.type === 'text' || !msg.type"
              class="cv-bubble__text"
            >{{ msg.content }}</p>

            <!-- ── Image ─────────────────────────────────── -->
            <div v-else-if="msg.type === 'image'" class="cv-media">
              <div v-if="!imageErrors[msg.file_url]" class="cv-media__frame">
                <img
                  :src="msg.file_url"
                  class="cv-media__img"
                  alt="Image"
                  @click="openImageModal(msg.file_url)"
                  @error="handleImageError(msg.file_url)"
                />
              </div>
              <div v-else class="cv-media__error">
                <i class="fas fa-image" aria-hidden="true"></i>
                <span>Image unavailable</span>
              </div>
              <p v-if="msg.content" class="cv-media__caption">{{ msg.content }}</p>
            </div>

            <!-- ── Video ─────────────────────────────────── -->
            <div v-else-if="msg.type === 'video'" class="cv-media">
              <div v-if="!videoErrors[msg.file_url]" class="cv-media__frame">
                <video
                  :src="msg.file_url"
                  class="cv-media__video"
                  controls
                  preload="metadata"
                  playsinline
                  @error="onVideoError(msg.file_url)"
                ></video>
              </div>
              <div v-else class="cv-media__error">
                <i class="fas fa-video" aria-hidden="true"></i>
                <span>Video unavailable</span>
              </div>
              <p v-if="msg.content" class="cv-media__caption">{{ msg.content }}</p>
            </div>

            <!-- ── File ──────────────────────────────────── -->
            <div v-else-if="msg.type === 'file'" class="cv-file">
              <div class="cv-file__icon" aria-hidden="true">
                <i :class="getFileIcon(msg.file_name)"></i>
              </div>
              <div class="cv-file__info">
                <span class="cv-file__name">{{ msg.file_name || 'File' }}</span>
                <span class="cv-file__size">{{ formatFileSize(msg.file_size) }}</span>
              </div>
              <a
                :href="msg.file_url"
                :download="msg.file_name"
                class="cv-file__dl"
                :aria-label="`Download ${msg.file_name || 'file'}`"
                @click.stop
              >
                <i class="fas fa-download" aria-hidden="true"></i>
              </a>
            </div>

            <!-- ── Meta ──────────────────────────────────── -->
            <div class="cv-bubble__meta">
              <time
                class="cv-bubble__time"
                :datetime="msg.timestamp"
              >{{ formatTime(msg.timestamp) }}</time>
              <i
                v-if="msg.sender === managerUsername"
                class="fas fa-check-double cv-bubble__status"
                aria-label="Delivered"
              ></i>
            </div>
          </div>
        </div>

      </div>

      <!-- Scroll-to-bottom pill -->
      <transition name="cv-pop">
        <button
          v-if="unreadCount > 0"
          class="cv-scroll-pill"
          @click="scrollToBottom(true)"
          :aria-label="`${unreadCount} unread messages — scroll to bottom`"
        >
          <i class="fas fa-arrow-down" aria-hidden="true"></i>
          <span>{{ unreadCount }}</span>
        </button>
      </transition>
    </main>

    <!-- ══ File preview bar ══════════════════════════════════ -->
    <div v-if="selectedFile" class="cv-preview" role="region" aria-label="File preview">
      <div class="cv-preview__card">
        <div v-if="filePreview.type === 'image'" class="cv-preview__thumb">
          <img :src="filePreview.url" alt="Preview" @error="onPreviewError" />
        </div>
        <div v-else-if="filePreview.type === 'video'" class="cv-preview__thumb">
          <video :src="filePreview.url" muted @error="onPreviewError"></video>
        </div>
        <div v-else class="cv-preview__file">
          <span class="cv-preview__file-icon" aria-hidden="true">
            <i :class="getFileIcon(selectedFile.name)"></i>
          </span>
          <div class="cv-preview__file-meta">
            <span class="cv-preview__file-name">{{ selectedFile.name }}</span>
            <span class="cv-preview__file-size">{{ formatFileSize(selectedFile.size) }}</span>
          </div>
        </div>
        <button
          class="cv-preview__remove"
          @click="removeFile"
          aria-label="Remove file"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <input
        v-model="fileCaption"
        placeholder="Add a caption (optional)…"
        class="cv-preview__caption-input"
        aria-label="File caption"
      />
    </div>

    <!-- ══ Footer ════════════════════════════════════════════ -->
    <footer class="cv-footer">

      <!-- Tool buttons -->
      <div class="cv-tools" role="toolbar" aria-label="Message tools">

        <!-- Attachment -->
        <div class="cv-tool" ref="attachTool">
          <button
            class="cv-tool__btn"
            :class="{ 'is-active': showAttachmentMenu }"
            @click="toggleMenu('attachment')"
            :aria-expanded="showAttachmentMenu.toString()"
            aria-label="Attach a file"
            title="Attach file"
          >
            <i class="fas fa-paperclip" aria-hidden="true"></i>
          </button>
          <transition name="cv-menu">
            <div
              v-if="showAttachmentMenu"
              class="cv-tool__popover cv-tool__popover--left"
              role="menu"
            >
              <button
                class="cv-tool__menu-item"
                role="menuitem"
                @click="selectFile('image')"
              >
                <i class="fas fa-image" aria-hidden="true"></i> Photo
              </button>
              <button
                class="cv-tool__menu-item"
                role="menuitem"
                @click="selectFile('video')"
              >
                <i class="fas fa-video" aria-hidden="true"></i> Video
              </button>
              <button
                class="cv-tool__menu-item"
                role="menuitem"
                @click="selectFile('file')"
              >
                <i class="fas fa-file-alt" aria-hidden="true"></i> Document
              </button>
            </div>
          </transition>
        </div>

        <!-- Emoji -->
        <div class="cv-tool" ref="emojiTool">
          <button
            class="cv-tool__btn"
            :class="{ 'is-active': showEmojiPicker }"
            @click="toggleMenu('emoji')"
            :aria-expanded="showEmojiPicker.toString()"
            aria-label="Insert emoji"
            title="Emoji"
          >
            <i class="fas fa-smile" aria-hidden="true"></i>
          </button>
          <transition name="cv-menu">
            <div
              v-if="showEmojiPicker"
              class="cv-tool__popover cv-tool__popover--left cv-emoji-wrap"
              ref="emojiPicker"
            >
              <Picker
                :data="emojiData"
                set="apple"
                title="Emoji"
                emoji="wave"
                @select="addEmoji"
              />
            </div>
          </transition>
        </div>

        <!-- Quick replies -->
        <div class="cv-tool" ref="quickTool">
          <button
            class="cv-tool__btn"
            :class="{ 'is-active': showQuickMessages }"
            @click="toggleMenu('quick')"
            :aria-expanded="showQuickMessages.toString()"
            aria-label="Quick replies"
            title="Quick replies"
          >
            <i class="fas fa-bolt" aria-hidden="true"></i>
          </button>
          <transition name="cv-menu">
            <div
              v-if="showQuickMessages"
              class="cv-tool__popover cv-quick-panel"
              role="listbox"
              aria-label="Quick replies"
            >
              <button
                v-for="(qm, idx) in quickMessagesList"
                :key="idx"
                class="cv-quick-item"
                role="option"
                @click="useQuickMessage(qm)"
              >
                <i :class="qm.icon" aria-hidden="true"></i>
                <span>{{ qm.text }}</span>
              </button>
            </div>
          </transition>
        </div>

      </div>

      <!-- Composer -->
      <div
        class="cv-composer"
        :class="{ 'is-focused': composerFocused }"
      >
      <textarea
        ref="messageInput"
        v-model="newMessage"
        placeholder="Reply to client…"
        class="cv-composer__input"
        rows="1"
        aria-label="Message"
        aria-multiline="true"
        @input="adjustTextareaHeight"
        @focus="composerFocused = true"
        @blur="composerFocused = false"
      ></textarea>
        <transition name="cv-send">
          <button
            v-if="newMessage.trim() || selectedFile"
            class="cv-send-btn"
            :disabled="sending"
            :aria-busy="sending.toString()"
            @click="sendMessage"
            aria-label="Send message"
          >
            <i
              :class="sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"
              aria-hidden="true"
            ></i>
          </button>
        </transition>
      </div>

    </footer>

    <!-- Hidden file inputs -->
    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      aria-hidden="true"
      tabindex="-1"
      style="display:none"
      @change="handleFileSelect"
    />
    <input
      ref="videoInput"
      type="file"
      accept="video/*"
      aria-hidden="true"
      tabindex="-1"
      style="display:none"
      @change="handleFileSelect"
    />
    <input
      ref="fileInput"
      type="file"
      accept="*/*"
      aria-hidden="true"
      tabindex="-1"
      style="display:none"
      @change="handleFileSelect"
    />

    <!-- ══ Image lightbox ════════════════════════════════════ -->
    <transition name="cv-fade">
      <div
        v-if="showImageModal"
        class="cv-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Image preview"
        @click.self="closeImageModal"
        @keydown.esc="closeImageModal"
      >
        <div class="cv-lightbox__frame">
          <img :src="selectedImageUrl" alt="Full-size image" />
          <button
            class="cv-lightbox__close"
            @click="closeImageModal"
            aria-label="Close image"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { Picker } from 'emoji-mart-vue';
import 'emoji-mart-vue/css/emoji-mart.css';
// FIX: import emoji data explicitly; previously `data` was never defined,
// causing :data="data" in the template to bind undefined.
import emojiData from 'emoji-mart-vue/data/apple.json';

export default {
  name: 'ChatView',

  components: { Picker },

  data() {
    return {
      // ── Messages & identity ─────────────────────────────
      messages: [],
      clientName: '',
      managerUsername: '',
      userType: 'client',          // 'client' | 'topup'
      userId: null,

      // ── Composition state ───────────────────────────────
      newMessage: '',
      composerFocused: false,
      sending: false,

      // ── Scroll / unread ─────────────────────────────────
      unreadCount: 0,
      autoScrollEnabled: true,
      lastMessageCount: 0,
      pollingInterval: null,

      // ── File handling ───────────────────────────────────
      selectedFile: null,
      fileCaption: '',
      filePreview: { type: null, url: null },
      previewError: false,

      // ── UI toggles ──────────────────────────────────────
      showAttachmentMenu: false,
      showEmojiPicker: false,
      showQuickMessages: false,
      showImageModal: false,
      selectedImageUrl: '',

      // ── Error / dedup tracking ──────────────────────────
      processedMessageIds: new Set(),
      imageErrors: {},
      videoErrors: {},

      // ── Emoji data (fixes the missing `data` binding) ───
      emojiData,

      // ── Quick replies ────────────────────────────────────
      quickMessagesList: [
        { text: 'Thank you for your message, I will get back to you shortly.', icon: 'fas fa-clock' },
        { text: 'Your request is being processed.',                             icon: 'fas fa-cog' },
        { text: 'Can you provide more details?',                                icon: 'fas fa-question-circle' },
        { text: 'Noted — we have recorded this on our end.',                    icon: 'fas fa-check-circle' },
        { text: 'Thank you for your patience.',                                  icon: 'fas fa-heart' },
      ],
    };
  },

  mounted() {
    this.userId   = this.$route.params.id;
    this.userType = this.$route.query.type === 'topup' ? 'topup' : 'client';
    this.fetchMessages(true);
    this.startPolling();
    document.addEventListener('click', this.handleGlobalClick);
    document.addEventListener('keydown', this.handleGlobalKey);
  },

  beforeDestroy() {
    clearInterval(this.pollingInterval);
    document.removeEventListener('click', this.handleGlobalClick);
    document.removeEventListener('keydown', this.handleGlobalKey);
    this.$refs.chatMessages?.removeEventListener('scroll', this.onScroll);
  },

  methods: {
    // ── Data fetching ─────────────────────────────────────

    async fetchMessages(markRead = true) {
      try {
        let response;

        if (this.userType === 'topup') {
          response = await axios.get(
            `/api/v1/manager/topup/conversation/${this.userId}/?mark_read=${markRead}`
          );
          this.processMessagesTopup(response.data.messages || []);
          this.clientName     = response.data.interlocutor_name || 'Top-up user';
          this.managerUsername = 'manager';
        } else {
          response = await axios.post('/api/v1/manager/get_chat_history/', {
            id_client: this.userId,
          });
          this.processMessages(response.data.list_message || []);
          this.clientName     = response.data.nom;
          this.managerUsername = response.data.priority;
        }

        this.$nextTick(() => {
          if (this.autoScrollEnabled || this.messages.length <= 1) {
            this.scrollToBottom();
          }
          // attach scroll listener once the container is rendered
          const container = this.$refs.chatMessages;
          if (container && !container._scrollListenerAttached) {
            container.addEventListener('scroll', this.onScroll);
            container._scrollListenerAttached = true;
          }
        });
      } catch (error) {
        if (error.response?.status === 404 && this.userType === 'topup') {
          this.messages   = [];
          this.clientName = 'Top-up user';
        } else {
          console.error('Error loading messages:', error);
        }
      }
    },

    processMessagesTopup(incoming) {
      if (!incoming.length) return;
      const normalized = incoming.map(msg => ({
        ...msg,
        type:    msg.message_type || 'text',
        content: msg.content || '',
      }));
      this._mergeMessages(normalized);
    },

    processMessages(incoming) {
      if (!incoming.length) return;
      this._mergeMessages(incoming);
    },

    /** Shared merge + dedup logic. */
    _mergeMessages(incoming) {
      const newItems = [];
      for (const msg of incoming) {
        const key = this._msgKey(msg);
        if (!this.processedMessageIds.has(key)) {
          this.processedMessageIds.add(key);
          newItems.push(msg);
        }
      }
      if (newItems.length) {
        this.messages = this._sortedUnique([...this.messages, ...newItems]);
      }
    },

    _msgKey(msg) {
      if (msg.id) return `id:${msg.id}`;
      const ts = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
      return `ts:${ts}:${msg.sender}:${msg.type}:${(msg.content || '').trim()}:${msg.file_url || ''}`;
    },

    _sortedUnique(list) {
      const map = new Map();
      list.forEach(m => { const k = this._msgKey(m); if (!map.has(k)) map.set(k, m); });
      return [...map.values()].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
    },

    startPolling() {
      this.pollingInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.fetchMessages(false);
        }
      }, 10_000);
    },

    // ── Sending ───────────────────────────────────────────

    async sendMessage() {
      const hasText = this.newMessage.trim() !== '';
      const hasFile = !!this.selectedFile;
      if ((!hasText && !hasFile) || this.sending) return;

      this.sending = true;
      try {
        const formData = new FormData();

        if (this.userType === 'topup') {
          formData.append('topup_id',     this.userId);
          formData.append('message',      this.newMessage.trim());
          formData.append('message_type', hasFile ? this.getFileType(this.selectedFile) : 'text');
          if (hasFile) formData.append('file', this.selectedFile);
          await axios.post('/api/v1/manager/topup/send/', formData);
        } else {
          formData.append('id_client', this.userId);
          if (hasFile) {
            formData.append('file',         this.selectedFile);
            formData.append('message_type', this.getFileType(this.selectedFile));
            const caption = this.fileCaption.trim() || this.newMessage.trim();
            if (caption) formData.append('message', caption);
          } else {
            formData.append('message',      this.newMessage.trim());
            formData.append('message_type', 'text');
          }
          await axios.post('/api/v1/manager/save_chat/', formData);
        }

        this.resetComposer();
        // brief delay so the server has time to persist before we re-fetch
        setTimeout(() => this.fetchMessages(true), 800);
      } catch (error) {
        console.error('Send error:', error);
        // TODO: replace alert with a proper snack-bar/toast notification
        alert('Failed to send message. Please try again.');
      } finally {
        this.sending          = false;
        this.showQuickMessages = false;
      }
    },

    resetComposer() {
      this.newMessage   = '';
      this.fileCaption  = '';
      this.removeFile();
      this.$nextTick(() => this.adjustTextareaHeight());
    },

    // ── Menu / popup helpers ──────────────────────────────

    /**
     * Exclusive toggle: opening one panel closes the others.
     * @param {'attachment'|'emoji'|'quick'} panel
     */
    toggleMenu(panel) {
      const state = {
        attachment: 'showAttachmentMenu',
        emoji:      'showEmojiPicker',
        quick:      'showQuickMessages',
      };
      const target = state[panel];
      if (!target) return;

      const wasOpen = this[target];
      // close all
      this.showAttachmentMenu = false;
      this.showEmojiPicker    = false;
      this.showQuickMessages  = false;
      // toggle the target
      if (!wasOpen) this[target] = true;
    },

    closeAllMenus() {
      this.showAttachmentMenu = false;
      this.showEmojiPicker    = false;
      this.showQuickMessages  = false;
    },

    handleGlobalClick(event) {
      // close any open popover when clicking outside its tool wrapper
      const tools = ['attachTool', 'emojiTool', 'quickTool'];
      const clickedInside = tools.some(ref =>
        this.$refs[ref]?.contains(event.target)
      );
      if (!clickedInside) this.closeAllMenus();
    },

    handleGlobalKey(event) {
      if (event.key === 'Escape') {
        if (this.showImageModal) { this.closeImageModal(); return; }
        this.closeAllMenus();
      }
    },

    // ── Scroll ────────────────────────────────────────────

    scrollToBottom(force = false) {
      const el = this.$refs.chatMessages;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      if (force) {
        this.unreadCount      = 0;
        this.autoScrollEnabled = true;
      }
    },

    onScroll() {
      const el = this.$refs.chatMessages;
      if (!el) return;
      this.autoScrollEnabled =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    },

    // ── Textarea ──────────────────────────────────────────

    adjustTextareaHeight() {
      const el = this.$refs.messageInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    },

    // ── Emoji ─────────────────────────────────────────────

    addEmoji(emoji) {
      this.newMessage += emoji.native;
      this.showEmojiPicker = false;
      this.$nextTick(() => {
        this.$refs.messageInput?.focus();
        this.adjustTextareaHeight();
      });
    },

    // ── Quick replies ─────────────────────────────────────

    useQuickMessage(qm) {
      this.newMessage       = qm.text;
      this.showQuickMessages = false;
      this.$nextTick(() => {
        this.$refs.messageInput?.focus();
        this.adjustTextareaHeight();
      });
    },

    // ── File handling ─────────────────────────────────────

    selectFile(type) {
      this.showAttachmentMenu = false;
      const refMap = { image: 'imageInput', video: 'videoInput', file: 'fileInput' };
      this.$refs[refMap[type]]?.click();
    },

    handleFileSelect(event) {
      const file   = event.target.files[0];
      if (!file) return;

      const MAX_VIDEO  = 2_000 * 1024 * 1024; // 2 GB
      const MAX_OTHER  =    10 * 1024 * 1024; // 10 MB
      const limit      = file.type.startsWith('video/') ? MAX_VIDEO : MAX_OTHER;

      if (file.size > limit) {
        alert(`Maximum file size is ${limit / (1024 * 1024)} MB.`);
        return;
      }

      this.selectedFile = file;
      this.previewError = false;
      this._buildPreview(file);
      event.target.value = ''; // allow re-selecting the same file
    },

    _buildPreview(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreview.url = e.target.result;
        if      (file.type.startsWith('image/')) this.filePreview.type = 'image';
        else if (file.type.startsWith('video/')) this.filePreview.type = 'video';
        else                                      this.filePreview.type = 'file';
      };
      reader.onerror = () => { this.previewError = true; };
      reader.readAsDataURL(file);
    },

    removeFile() {
      this.selectedFile = null;
      this.fileCaption  = '';
      this.filePreview  = { type: null, url: null };
      this.previewError = false;
    },

    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image';
      if (file.type.startsWith('video/')) return 'video';
      return 'file';
    },

    // ── Errors ────────────────────────────────────────────

    handleImageError(url) { this.$set(this.imageErrors, url, true); },
    onVideoError(url)     { this.$set(this.videoErrors, url, true); },
    onPreviewError()      { this.previewError = true; },

    // ── Lightbox ──────────────────────────────────────────

    openImageModal(url) {
      if (this.imageErrors[url]) return;
      this.selectedImageUrl = url;
      this.showImageModal   = true;
    },

    closeImageModal() {
      this.showImageModal   = false;
      this.selectedImageUrl = '';
    },

    // ── Formatting helpers ────────────────────────────────

    formatTime(timestamp) {
      if (!timestamp) return '';
      const d     = new Date(timestamp);
      const today = new Date();
      const isToday =
        d.getDate()     === today.getDate()     &&
        d.getMonth()    === today.getMonth()    &&
        d.getFullYear() === today.getFullYear();

      return isToday
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString([], { day: '2-digit', month: '2-digit',
                                     hour: '2-digit', minute: '2-digit' });
    },

    getFileIcon(name) {
      if (!name) return 'fas fa-file';
      const ext = name.split('.').pop().toLowerCase();
      const map = {
        pdf:  'fas fa-file-pdf',
        doc:  'fas fa-file-word',  docx: 'fas fa-file-word',
        xls:  'fas fa-file-excel', xlsx: 'fas fa-file-excel',
        ppt:  'fas fa-file-powerpoint', pptx: 'fas fa-file-powerpoint',
        txt:  'fas fa-file-alt',
        jpg:  'fas fa-file-image', jpeg: 'fas fa-file-image',
        png:  'fas fa-file-image', gif:  'fas fa-file-image',
        mp4:  'fas fa-file-video', mov:  'fas fa-file-video',
        zip:  'fas fa-file-archive', rar: 'fas fa-file-archive',
      };
      return map[ext] || 'fas fa-file';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      const i     = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i];
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════════════════════
   DESIGN TOKENS — shared with dashboard (dm-root)
══════════════════════════════════════════════════════ */
.cv-root {
  /* palette */
  --c-bg:             #f0f2fa;   /* messages area */
  --c-surface:        #ffffff;
  --c-border:         #e8eaf2;
  --c-primary:        #5c6bc0;
  --c-primary-dark:   #4a58af;
  --c-primary-light:  #eef0fc;
  --c-text:           #1a1c2e;
  --c-text-med:       #5a5d78;
  --c-text-faint:     #a0a3bc;
  --c-online:         #22c55e;
  --c-danger:         #ef4444;
  /* bubble colours */
  --c-sent:           #5c6bc0;
  --c-sent-txt:       #ffffff;
  --c-recv:           #ffffff;
  --c-recv-txt:       #1a1c2e;
  /* shadows */
  --shadow-card:   0 1px 3px rgba(26,28,46,.05), 0 4px 20px rgba(92,107,192,.07);
  --shadow-bubble: 0 1px 4px rgba(26,28,46,.09);
  --shadow-pop:    0 8px 28px rgba(26,28,46,.14);
  --shadow-modal:  0 24px 64px rgba(26,28,46,.22);
  /* geometry */
  --radius:       12px;
  --radius-sm:     8px;
  --radius-pill: 9999px;
  --font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  background: var(--c-surface);
  border-radius: var(--radius);
  border: 1px solid var(--c-border);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  font-family: var(--font);
  color: var(--c-text);
}

/* ══════════════════════════════════════════════════════
   HEADER
══════════════════════════════════════════════════════ */
.cv-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
  flex-shrink: 0;
}

.cv-header__user {
  display: flex;
  align-items: center;
  gap: 11px;
}

/* avatar */
.cv-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.cv-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--c-primary-light);
  display: block;
}

.cv-status-dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 10px;
  height: 10px;
  background: var(--c-online);
  border-radius: 50%;
  border: 2px solid var(--c-surface);
}

/* identity text */
.cv-header__meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.cv-header__name {
  font-size: 15px;
  font-weight: 700;
  color: var(--c-text);
  line-height: 1.2;
}

.cv-header__role {
  font-size: 11px;
  color: var(--c-text-faint);
}

/* manager badge */
.cv-manager-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: var(--c-primary-light);
  color: var(--c-primary);
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: 600;
}

/* ══════════════════════════════════════════════════════
   MESSAGES AREA
══════════════════════════════════════════════════════ */
.cv-messages {
  flex: 1;
  overflow-y: auto;
  background: var(--c-bg);
  padding: 18px 16px;
  position: relative;
  scroll-behavior: smooth;
}

.cv-messages::-webkit-scrollbar {
  width: 4px;
}
.cv-messages::-webkit-scrollbar-thumb {
  background: var(--c-border);
  border-radius: 2px;
}

.cv-messages__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* ── Message rows ──────────────────────────────────── */
.cv-row {
  display: flex;
}

.cv-row--sent     { justify-content: flex-end; }
.cv-row--received { justify-content: flex-start; }

/* ── Bubble ────────────────────────────────────────── */
.cv-bubble {
  max-width: 72%;
  padding: 10px 14px;
  box-shadow: var(--shadow-bubble);
  word-break: break-word;
  overflow-wrap: break-word;
  position: relative;
}

.cv-bubble--sent {
  background: var(--c-sent);
  color: var(--c-sent-txt);
  /* tail on sent side */
  border-radius: 18px 18px 5px 18px;
}

.cv-bubble--received {
  background: var(--c-recv);
  color: var(--c-recv-txt);
  /* tail on received side */
  border-radius: 18px 18px 18px 5px;
}

.cv-bubble__text {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.5;
}

/* meta row (time + status) */
.cv-bubble__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 2px;
}

.cv-bubble__time {
  font-size: 10px;
  line-height: 1;
}

.cv-bubble--sent .cv-bubble__time {
  color: rgba(255,255,255,.65);
}

.cv-bubble--received .cv-bubble__time {
  color: var(--c-text-faint);
}

.cv-bubble__status {
  font-size: 10px;
  color: rgba(255,255,255,.7);
}

/* ── Media ─────────────────────────────────────────── */
.cv-media {
  min-width: 180px;
  max-width: 280px;
}

.cv-media__frame {
  border-radius: 10px;
  overflow: hidden;
}

.cv-media__img {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  cursor: pointer;
  transition: opacity .15s ease;
}

.cv-media__img:hover { opacity: .9; }

.cv-media__video {
  display: block;
  width: 100%;
  max-height: 220px;
  border-radius: 10px;
}

.cv-media__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px;
  font-size: 12px;
  border-radius: 10px;
}

.cv-bubble--sent .cv-media__error {
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.7);
}

.cv-bubble--received .cv-media__error {
  background: var(--c-bg);
  color: var(--c-text-faint);
}

.cv-media__caption {
  margin: 6px 0 4px;
  font-size: 13px;
  line-height: 1.4;
}

.cv-bubble--sent .cv-media__caption     { color: rgba(255,255,255,.85); }
.cv-bubble--received .cv-media__caption { color: var(--c-text-med); }

/* ── File attachment ───────────────────────────────── */
.cv-file {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: var(--radius-sm);
  min-width: 220px;
}

.cv-bubble--sent .cv-file {
  background: rgba(255,255,255,.14);
}

.cv-bubble--received .cv-file {
  background: var(--c-bg);
}

.cv-file__icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 16px;
  flex-shrink: 0;
}

.cv-bubble--sent .cv-file__icon {
  background: rgba(255,255,255,.2);
  color: #fff;
}

.cv-bubble--received .cv-file__icon {
  background: var(--c-primary-light);
  color: var(--c-primary);
}

.cv-file__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cv-file__name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cv-file__size {
  font-size: 11px;
  opacity: .7;
}

.cv-file__dl {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  text-decoration: none;
  transition: background .15s;
  flex-shrink: 0;
}

.cv-bubble--sent .cv-file__dl {
  background: rgba(255,255,255,.18);
  color: #fff;
}

.cv-bubble--sent .cv-file__dl:hover {
  background: rgba(255,255,255,.3);
}

.cv-bubble--received .cv-file__dl {
  background: var(--c-primary-light);
  color: var(--c-primary);
}

.cv-bubble--received .cv-file__dl:hover {
  background: var(--c-border);
}

/* ── Scroll pill ───────────────────────────────────── */
.cv-scroll-pill {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: var(--c-primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(92,107,192,.35);
  transition: background .15s;
}

.cv-scroll-pill:hover {
  background: var(--c-primary-dark);
}

/* ══════════════════════════════════════════════════════
   FILE PREVIEW BAR
══════════════════════════════════════════════════════ */
.cv-preview {
  background: var(--c-surface);
  border-top: 1px solid var(--c-border);
  padding: 10px 16px;
  flex-shrink: 0;
}

.cv-preview__card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--c-bg);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  position: relative;
  border: 1px solid var(--c-border);
}

/* thumb (image / video) */
.cv-preview__thumb {
  width: 50px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--c-border);
}

.cv-preview__thumb img,
.cv-preview__thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* generic file */
.cv-preview__file {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cv-preview__file-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: var(--c-primary-light);
  color: var(--c-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.cv-preview__file-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cv-preview__file-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--c-text);
}

.cv-preview__file-size {
  font-size: 11px;
  color: var(--c-text-faint);
}

/* remove button */
.cv-preview__remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  background: rgba(26,28,46,.2);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  transition: background .15s;
}

.cv-preview__remove:hover {
  background: rgba(26,28,46,.45);
}

/* caption input */
.cv-preview__caption-input {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 6px 10px;
  box-sizing: border-box;
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-family: var(--font);
  color: var(--c-text);
  background: var(--c-surface);
  outline: none;
  transition: border-color .2s;
}

.cv-preview__caption-input:focus {
  border-color: var(--c-primary);
}

/* ══════════════════════════════════════════════════════
   FOOTER
══════════════════════════════════════════════════════ */
.cv-footer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 14px 12px;
  background: var(--c-surface);
  border-top: 1px solid var(--c-border);
  flex-shrink: 0;
}

/* ── Tool buttons ──────────────────────────────────── */
.cv-tools {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  align-items: flex-end;
}

.cv-tool {
  position: relative;
}

.cv-tool__btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--c-text-faint);
  font-size: 15px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background .15s, color .15s;
}

.cv-tool__btn:hover,
.cv-tool__btn.is-active {
  background: var(--c-primary-light);
  color: var(--c-primary);
}

/* popover (shared base) */
.cv-tool__popover {
  position: absolute;
  bottom: calc(100% + 6px);
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-pop);
  z-index: 200;
  overflow: hidden;
}

.cv-tool__popover--left { left: 0; }

/* attachment menu */
.cv-tool__menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  background: none;
  font-size: 13px;
  font-family: var(--font);
  color: var(--c-text-med);
  cursor: pointer;
  transition: background .13s;
  white-space: nowrap;
  text-align: left;
}

.cv-tool__menu-item:hover {
  background: var(--c-primary-light);
  color: var(--c-primary);
}

/* emoji picker container */
.cv-emoji-wrap {
  width: 338px; /* default emoji-mart width */
}

/* quick replies panel */
.cv-quick-panel {
  width: 300px;
  max-height: 210px;
  overflow-y: auto;
  padding: 6px;
}

.cv-quick-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: none;
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  font-family: var(--font);
  color: var(--c-text-med);
  cursor: pointer;
  text-align: left;
  line-height: 1.4;
  transition: background .13s;
}

.cv-quick-item:hover {
  background: var(--c-primary-light);
  color: var(--c-primary);
}

.cv-quick-item i {
  margin-top: 1px;
  flex-shrink: 0;
  font-size: 13px;
}

/* ── Composer ──────────────────────────────────────── */
.cv-composer {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border: 1.5px solid var(--c-border);
  border-radius: 20px;
  padding: 6px 8px 6px 12px;
  background: var(--c-bg);
  transition: border-color .2s, background .2s;
   min-width: 0;
}

.cv-composer.is-focused {
  border-color: var(--c-primary);
  background: var(--c-surface);
}

.cv-composer__input {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-size: 14px;
  font-family: var(--font);
  color: var(--c-text);
  line-height: 1.5;
  max-height: 120px;
  padding: 4px 0;
  min-width: 0;
}

.cv-composer__input::placeholder {
  color: var(--c-text-faint);
}

/* send button */
.cv-send-btn {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-primary);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  transition: background .15s, transform .15s;
  box-shadow: 0 2px 8px rgba(92,107,192,.3);
}

.cv-send-btn:hover:not(:disabled) {
  background: var(--c-primary-dark);
  transform: scale(1.06);
}

.cv-send-btn:disabled {
  opacity: .55;
  cursor: not-allowed;
  transform: none;
}

/* ══════════════════════════════════════════════════════
   LIGHTBOX
══════════════════════════════════════════════════════ */
.cv-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(10,10,20,.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.cv-lightbox__frame {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-modal);
}

.cv-lightbox__frame img {
  display: block;
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
}

.cv-lightbox__close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: rgba(10,10,20,.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: background .15s;
}

.cv-lightbox__close:hover {
  background: rgba(10,10,20,.85);
}

/* ══════════════════════════════════════════════════════
   TRANSITIONS
══════════════════════════════════════════════════════ */

/* overlay/lightbox */
.cv-fade-enter-active,
.cv-fade-leave-active { transition: opacity .22s ease; }
.cv-fade-enter,
.cv-fade-leave-to     { opacity: 0; }

/* popover menus */
.cv-menu-enter-active,
.cv-menu-leave-active { transition: opacity .16s ease, transform .16s ease; }
.cv-menu-enter,
.cv-menu-leave-to     { opacity: 0; transform: translateY(6px) scale(.97); }

/* scroll pill */
.cv-pop-enter-active,
.cv-pop-leave-active  { transition: opacity .2s ease, transform .2s ease; }
.cv-pop-enter,
.cv-pop-leave-to      { opacity: 0; transform: scale(.8); }

/* send button */
.cv-send-enter-active,
.cv-send-leave-active { transition: opacity .15s ease, transform .15s ease; }
.cv-send-enter,
.cv-send-leave-to     { opacity: 0; transform: scale(.6); }

/* ══════════════════════════════════════════════════════
   REDUCED MOTION — respect accessibility preference
══════════════════════════════════════════════════════ */
@media (prefers-reduced-motion: reduce) {
  .cv-fade-enter-active,  .cv-fade-leave-active,
  .cv-menu-enter-active,  .cv-menu-leave-active,
  .cv-pop-enter-active,   .cv-pop-leave-active,
  .cv-send-enter-active,  .cv-send-leave-active,
  .cv-send-btn,
  .cv-media__img,
  .cv-tool__btn,
  .cv-composer {
    transition: none !important;
    animation: none !important;

  }
}

/* ══════════════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .cv-root {
    height: calc(100vh - 80px);
    border-radius: 0;
    border-left: none;
    border-right: none;
  }

  .cv-bubble { max-width: 86%; }

  .cv-media,
  .cv-file  { max-width: 240px; }

  .cv-emoji-wrap  { width: 100vw; }
  .cv-quick-panel { width: 240px; }

  .cv-header { padding: 10px 14px; }

  .cv-footer { padding: 8px 10px 10px; }
}
</style>
